import { createRouter, createWebHistory, type Router, type RouteRecordRaw } from 'vue-router';
import { isAuthenticated } from '@/auth/authService';
import { resolveEntryLink } from '@/router/entryLinks';
import { RouteNames } from '@/router/routeNames';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: RouteNames.Home,
    component: () => import('@/views/home/HomeView.vue'),
    // Inbound links (QR codes, mobile app, Nexi return) all land on "/".
    beforeEnter: (to) => resolveEntryLink(to.query, isAuthenticated.value) ?? true,
  },
  { path: '/privacy', name: RouteNames.Privacy, component: () => import('@/views/privacy/PrivacyView.vue') },
  { path: '/cookies', name: RouteNames.Cookies, component: () => import('@/views/cookies/CookiesView.vue') },
  { path: '/post/:id', name: RouteNames.BlogPost, component: () => import('@/views/blog/BlogPostView.vue') },
  // Kept compatible with the legacy "/qr-check/:id" form; the hash is read from ?id= first.
  { path: '/qr-check/:id?', name: RouteNames.QrCheck, component: () => import('@/views/qr/QrCheckView.vue') },
  {
    path: '/mobileapp-registration/:ma?',
    name: RouteNames.MobileAppRegistration,
    component: () => import('@/views/mobileapp/MobileAppRegistrationView.vue'),
  },
  {
    path: '/pre-customer',
    name: RouteNames.PreCustomer,
    component: () => import('@/views/customer/PreCustomerView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/customer',
    name: RouteNames.Customer,
    component: () => import('@/views/customer/CustomerView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/vendon/payment/result',
    name: RouteNames.VendonTopUp,
    component: () => import('@/views/vendon/VendonTopUpView.vue'),
  },
  { path: '/:pathMatch(.*)*', redirect: { name: RouteNames.Home } },
];

/**
 * Creates the router. Must be called AFTER the MSAL redirect has been handled and the
 * post-login URL restored: createWebHistory() captures window.location when created.
 */
export function createAppRouter(): Router {
  const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior: () => ({ top: 0 }),
  });

  // The MSAL session is fully restored before the router exists (see main.ts),
  // so this check is synchronous and never races with the B2C redirect handling.
  router.beforeEach((to) => {
    if (to.meta.requiresAuth && !isAuthenticated.value) {
      return { name: RouteNames.Home };
    }
    return true;
  });

  return router;
}
